import axios from "axios";
import { Fragment, useCallback, useEffect, useState } from "react";
import { ThreeDots } from "react-loader-spinner";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Footer from "../../layout/Footer";
import Loader from "../../layout/Loader";
import Menu from "../../layout/Menu";
import DriftSummaryCards, { usd } from "../../components/DriftSummaryCards";

/**
 * Pay-As-You-Go Invoices.
 *
 * What the monthly invoice job did for every company and month, and the two manual levers for
 * when it did not:
 *
 *   Generate & invoice  the invoice was never raised (job down on its day, Stripe failure,
 *                       retries used up - the nightly sweep only runs on days 1-5)
 *   Collect payment     the invoice was raised but the card was never charged / was declined
 *
 * The list makes no Stripe calls. Generate previews first and only touches Stripe after the
 * confirm. Both actions are safe to repeat: the server will not raise a second invoice for the
 * same company and month, and only pays an invoice Stripe still reports as open.
 */

const STATES = [
  { value: "needs_action", label: "Needs action" },
  { value: "all", label: "Everything" },
  { value: "not_started", label: "Not invoiced yet" },
  { value: "processing", label: "Processing" },
  { value: "failed", label: "Failed to invoice" },
  { value: "invoiced", label: "Invoiced, unpaid" },
  { value: "payment_failed", label: "Payment failed" },
  { value: "paid", label: "Paid" },
  { value: "no_charge", label: "No charge" },
];

const STATE_BADGE = {
  not_started: { cls: "bg-warning", label: "Not invoiced" },
  processing: { cls: "bg-info", label: "Processing" },
  failed: { cls: "bg-danger", label: "Failed" },
  invoiced: { cls: "bg-primary", label: "Invoiced - unpaid" },
  payment_failed: { cls: "bg-danger", label: "Payment failed" },
  paid: { cls: "bg-success", label: "Paid" },
  no_charge: { cls: "bg-secondary", label: "No charge" },
  void: { cls: "bg-secondary", label: "Void" },
};

const STATE_HELP = {
  not_started: "Usage is pending in a closed month and no invoice run exists. The job never reached this company.",
  processing: "A run exists but has not finished. If it is past its invoice day, generate it.",
  failed: "The invoice could not be built or sent to Stripe. See the error.",
  invoiced: "The invoice was raised but the card has not been charged. Use Collect payment.",
  payment_failed: "Stripe tried the card and it failed. The customer is in a grace period.",
};

const API = `${process.env.REACT_APP_base_url}api/masterAdmin/payg-invoices`;

const post = (path, data) =>
  axios.post(
    `${API}${path}`,
    { auth_code: process.env.REACT_APP_AUTH_CODE, ...data },
    { headers: { "Content-Type": "application/json" } }
  );

const PaygInvoices = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [timezone, setTimezone] = useState("");
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 0, totalCount: 0 });
  const [loader, setLoader] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [state, setState] = useState("needs_action");
  const [lookback, setLookback] = useState(6);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    document.getElementById("page-loader").style.display = "none";
    document.getElementById("page-container").classList.add("show");
  }, []);

  const fetchRows = useCallback(async () => {
    setLoader(true);
    try {
      const response = await post("", {
        page: currentPage,
        limit: pageSize,
        state,
        lookback,
        search: debouncedSearch,
      });

      if (response.data.success === true) {
        const data = response.data.data;
        setRows(data.rows || []);
        setSummary(data.summary || null);
        setTimezone(data.timezone || "");
        setPagination(data.pagination || { currentPage: 1, totalPages: 0, totalCount: 0 });
      } else {
        toast.error(response.data.message || "Could not load invoices");
      }
    } catch (error) {
      console.error("Error loading PAYG invoices:", error);
      toast.error(error.response?.data?.message || "Could not load invoices");
    } finally {
      setLoader(false);
    }
  }, [currentPage, pageSize, state, lookback, debouncedSearch]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const rowKey = (row) => `${row.companyId}:${row.billingPeriodKey}`;

  /** Preview, confirm, then raise and charge the invoice for one company and month. */
  const generate = async (row) => {
    setBusy(rowKey(row));
    try {
      const preview = await post("/generate", {
        companyId: row.companyId,
        billingPeriodKey: row.billingPeriodKey,
        apply: false,
      });
      if (!preview.data.success) {
        toast.error(preview.data.message);
        return;
      }

      const p = preview.data.data;
      const ok = window.confirm(
        `Generate & invoice ${row.companyName} for ${p.periodLabel}?\n\n` +
          `Topos: ${p.topoCount}\n` +
          `Usage: ${usd(p.usageAmount)}\n` +
          `Base fee (next month): ${usd(p.baseCharge)}\n` +
          `Total: ${usd(p.total)}\n\n` +
          "This creates the Stripe invoice and charges the card on file immediately."
      );
      if (!ok) return;

      const response = await post("/generate", {
        companyId: row.companyId,
        billingPeriodKey: row.billingPeriodKey,
        apply: true,
      });
      if (response.data.success) {
        const note = response.data.data?.chargeNote;
        if (note) toast.warn(`${response.data.message} ${note}`);
        else toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
      fetchRows();
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not generate the invoice.");
      fetchRows();
    } finally {
      setBusy(null);
    }
  };

  /** Charge an already-raised, unpaid invoice with the customer's default card. */
  const collect = async (row) => {
    if (
      !window.confirm(
        `Charge ${row.companyName}'s card on file for the ${row.periodLabel} invoice` +
          `${row.totalAmount !== null ? ` (${usd(row.totalAmount)})` : ""}?`
      )
    ) return;

    setBusy(rowKey(row));
    try {
      const runId = row.runId;
      const response = await post("/collect", { runId });
      if (response.data.success) toast.success(response.data.message);
      else toast.error(response.data.message);
      fetchRows();
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not collect the payment.");
      fetchRows();
    } finally {
      setBusy(null);
    }
  };

  const stateBadge = (row) => {
    const badge = STATE_BADGE[row.state] || { cls: "bg-secondary", label: row.state };
    return (
      <span className={`badge ${badge.cls}`} title={STATE_HELP[row.state] || ""} style={{ cursor: "help" }}>
        {badge.label}
      </span>
    );
  };

  return (
    <Fragment>
      <Loader />
      <div id="page-container" className="fade page-sidebar-fixed page-header-fixed">
        <Menu />
        <div id="content" className="content">
          <ol className="breadcrumb float-xl-right">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/company">Company</Link>
            </li>
            <li className="breadcrumb-item active">Pay As You Go Invoices</li>
          </ol>

          <h1 className="page-header">Pay As You Go Invoices</h1>

          <p className="text-muted" style={{ marginTop: -10, marginBottom: 18 }}>
            Each month's invoice covers the whole previous calendar month{timezone ? ` (${timezone} time)` : ""}.
            The invoice day only decides when it is raised. If the automatic run missed a company or the
            card was never charged, fix it here. Drift between topos and money is on{" "}
            <Link to="/payg-drift">Pay As You Go Drift</Link>.
          </p>

          {summary && (
            <DriftSummaryCards
              items={[
                { label: "Needs action", value: String(summary.needsAttention), tone: "alert-danger" },
                { label: "Invoiced, unpaid", value: String(summary.byState?.invoiced || 0), tone: "alert-warning" },
                { label: "Payment failed", value: String(summary.byState?.payment_failed || 0), tone: "alert-danger" },
                { label: "Paid", value: String(summary.byState?.paid || 0), tone: "alert-success" },
              ]}
            />
          )}

          <div className="card">
            <div className="card-body">
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                <input
                  type="text"
                  className="form-control"
                  style={{ width: 240 }}
                  placeholder="Search company or email"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                />

                <select
                  className="form-select"
                  style={{ width: 190 }}
                  title="Show"
                  value={state}
                  onChange={(e) => { setState(e.target.value); setCurrentPage(1); }}
                >
                  {STATES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>

                <select
                  className="form-select"
                  style={{ width: 170 }}
                  title="How many closed months back to look"
                  value={lookback}
                  onChange={(e) => { setLookback(Number(e.target.value)); setCurrentPage(1); }}
                >
                  {[1, 3, 6, 12].map((n) => (
                    <option key={n} value={n}>{n === 1 ? "Last month" : `Last ${n} months`}</option>
                  ))}
                </select>

                <select
                  className="form-select"
                  style={{ width: 120 }}
                  title="Rows per page"
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                >
                  {[20, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
                </select>

                <button type="button" className="btn btn-outline-secondary" onClick={fetchRows}>
                  Refresh
                </button>
              </div>

              <div className="table-responsive">
                <table className="table table-striped table-bordered">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Month</th>
                      <th>Status</th>
                      <th>Amount</th>
                      <th>Details</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loader ? (
                      <tr><td colSpan={6} className="text-center">
                        <ThreeDots color="#348fe2" height={40} width={40} />
                      </td></tr>
                    ) : rows.length === 0 ? (
                      <tr><td colSpan={6} className="text-center text-success">
                        <i className="fa fa-check-circle mr-2"></i>
                        Nothing needs action. Every closed month is invoiced and paid.
                      </td></tr>
                    ) : (
                      rows.map((row) => {
                        const key = rowKey(row);
                        const working = busy === key;
                        return (
                          <tr key={key}>
                            <td>
                              <strong>{row.companyName}</strong><br />
                              <small className="text-muted">{row.companyEmail}</small>
                            </td>
                            <td>
                              {row.periodLabel}<br />
                              <small className="text-muted">
                                due {row.scheduledLabel} (day {row.invoiceDayOfMonth})
                              </small>
                            </td>
                            <td>
                              {stateBadge(row)}
                              {row.needsAttention && (
                                <i className="fa fa-exclamation-triangle text-warning ml-2" title="Needs action"></i>
                              )}
                            </td>
                            <td>
                              {row.totalAmount !== null
                                ? usd(row.totalAmount)
                                : row.pendingTopoCount > 0
                                ? <>{usd(row.pendingAmount)}<br /><small className="text-muted">{row.pendingTopoCount} topo(s) pending</small></>
                                : <span className="text-muted">&mdash;</span>}
                            </td>
                            <td>
                              {row.attempts > 0 && <small className="text-muted">attempts: {row.attempts}</small>}
                              {row.lastError && (
                                <div><small className="text-danger" title={row.lastError}>
                                  {row.lastError.slice(0, 90)}
                                </small></div>
                              )}
                              {!row.hasStripeCustomer && (
                                <div><small className="text-danger">No Stripe customer</small></div>
                              )}
                              {!row.isPayg && (
                                <div><small className="text-muted">No longer Pay-As-You-Go</small></div>
                              )}
                              {row.stripeInvoiceUrl && (
                                <div>
                                  <a href={row.stripeInvoiceUrl} target="_blank" rel="noopener noreferrer">
                                    Stripe invoice <i className="fa fa-external-link-alt"></i>
                                  </a>
                                </div>
                              )}
                            </td>
                            <td>
                              {row.canGenerate && row.isPayg && row.hasStripeCustomer && (
                                <button
                                  className="btn btn-sm btn-primary mr-1"
                                  disabled={working}
                                  onClick={() => generate(row)}
                                  title="Raise the invoice for this month and charge the card on file"
                                >
                                  {working ? <i className="fa fa-spinner fa-spin"></i> : "Generate & invoice"}
                                </button>
                              )}
                              {row.canCollect && (
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  disabled={working}
                                  onClick={() => collect({ ...row, runId: row.runId })}
                                  title="Charge the card on file for this unpaid invoice"
                                >
                                  {working ? <i className="fa fa-spinner fa-spin"></i> : "Collect payment"}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {pagination.totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalCount} rows)
                  </small>
                  <div>
                    <button
                      className="btn btn-sm btn-outline-secondary mr-2"
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={currentPage >= pagination.totalPages}
                      onClick={() => setCurrentPage((p) => p + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </Fragment>
  );
};

export default PaygInvoices;
